"use client";

import Image from "@/infrastructure/router/StaticImage";
import { ProjectCanvasWorkspace } from "./ProjectCanvasWorkspace";
import type {
	PublicProjectOwner,
	PublicProjectShare,
} from "@/domain/projectCanvas/PublicProjectShare";
import { useTranslation } from "@/i18n/ArtistI18n";

export function PublicProjectShareView({
	share,
	slug,
}: Readonly<{
	share: PublicProjectShare;
	slug: string;
}>) {
	const { t } = useTranslation();
	const title = share.document.project.title.value;
	const appLink = `artist://project-share/${encodeURIComponent(slug)}`;

	return (
		<main style={{ minHeight: "100dvh", background: "var(--ds-background)" }}>
			<header
				style={{
					display: "flex",
					alignItems: "center",
					gap: 12,
					padding: "12px 18px",
					borderBottom: "1px solid var(--ds-separator)",
					background: "color-mix(in srgb, var(--ds-background) 84%, transparent)",
					backdropFilter: "blur(22px)",
					position: "relative",
					zIndex: 4,
				}}
			>
				<OwnerAvatar owner={share.owner} />
				<div style={{ minWidth: 0, flex: 1 }}>
					<div
						style={{
							fontSize: 12,
							fontWeight: 650,
							color: "var(--ds-secondary-label)",
						}}
					>
						{t("share.readOnlyBy", { name: share.owner.displayName })}
					</div>
					<h1
						style={{
							fontSize: 17,
							fontWeight: 720,
							margin: 0,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{title}
					</h1>
				</div>
				<a
					href={appLink}
					style={{
						background: "var(--ds-accent)",
						color: "white",
						padding: "10px 15px",
						borderRadius: 999,
						fontSize: 14,
						fontWeight: 700,
						textDecoration: "none",
						whiteSpace: "nowrap",
					}}
				>
					{t("share.openInArtist")}
				</a>
			</header>

			<ProjectCanvasWorkspace
				initialDocument={share.document}
				readOnly
			/>
		</main>
	);
}

function OwnerAvatar({ owner }: Readonly<{ owner: PublicProjectOwner }>) {
	if (owner.avatarUrl !== null) {
		return (
			<Image
				src={owner.avatarUrl}
				alt=""
				width={42}
				height={42}
				unoptimized
				style={{ borderRadius: "50%", objectFit: "cover" }}
			/>
		);
	}

	return (
		<div
			aria-hidden="true"
			style={{
				width: 42,
				height: 42,
				borderRadius: "50%",
				display: "grid",
				placeItems: "center",
				background: "var(--ds-fill-secondary)",
				fontWeight: 760,
			}}
		>
			{owner.displayName.slice(0, 1).toUpperCase()}
		</div>
	);
}
